<template>
    <div data-component="AmsPriceScale" class="flex justify-center gap-16 py-24">
        <div
            class="h-[102px] solid transform flex items-center relative bg-white p-16 border rounded-10 shadow"
            :style="position"
        >
            <div
                class="absolute overflow-hidden basis-1/2 after:absolute after:w-8 after:h-8 after:bg-white after:shadow after:rotate-45 after:border-t after:border-r w-16 h-32 -right-16 top-[50%] -translate-y-[50%] after:rounded-tr-[2px] after:left-0 after:top-[50%] after:-translate-x-[50%] after:-translate-y-[50%]"
            ></div>
            <div class="flex flex-col screen-m:mr-32">
                <ams-rating-lights :priceClass="priceClass" />
                <div class="mt-10">Dieses Angebot</div>
                <div class="font-bold">{{ commonUtils.thousandSeparator(price) }} €</div>
            </div>
        </div>

        <div class="flex flex-col gap-2">
            <div v-for="i in 5" :key="i" class="flex items-center gap-16">
                <div
                    class="w-8 h-60"
                    :class="{
                        'bg-color-rating-darkgreen-100 rounded-t-10': i == PriceClass.VeryGoodPrice,
                        'bg-color-rating-mediumgreen-100': i == PriceClass.GoodPrice,
                        'bg-color-rating-lightgreen-100': i == PriceClass.FairPrice,
                        'bg-color-rating-lightorange-100': i == PriceClass.IncreasedPrice,
                        'bg-color-rating-mediumorange-100 rounded-b-10': i == PriceClass.HighPrice,
                    }"
                ></div>
                <div class="flex flex-col">
                    <div class="font-bold">{{ determinePriceTextBy(i) }}</div>
                    <div>
                        {{ commonUtils.thousandSeparator(priceIndicatorClasses[i - 1]) }} € -
                        {{ commonUtils.thousandSeparator(priceIndicatorClasses[i]) }} €
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { PropType, computed } from 'vue'
import { PriceClass } from '../../../services/amsSearch/amsSearch.interface'
import { determinePriceTextBy } from '../AmsPriceIndicatorHelper'
import inRange from 'lodash/inRange'
import { commonUtils } from '@sunrise/shared'
import AmsRatingLights from '../AmsRatingLights/AmsRatingLights.vue'

const TOOLTIP_HEIGHT = 102
const TENDENCY_HEIGHT = 20

const props = defineProps({
    priceClass: { type: Number as PropType<PriceClass>, required: true },
    priceIndicatorClasses: { type: Array<number>, required: true },
    price: { type: Number, required: true },
})

const startPositionMap = new Map([
    [PriceClass.VeryGoodPrice, -10],
    [PriceClass.GoodPrice, 52],
    [PriceClass.FairPrice, 114],
    [PriceClass.IncreasedPrice, 176],
    [PriceClass.HighPrice, 238],
])

const position = computed(() => calculateTooltipPosition())

function calculateTooltipPosition() {
    const priceTendency = calculatePriceTendency()
    const startPosition = startPositionMap.get(props.priceClass)
    if (startPosition && priceTendency) {
        const tooltipPosition = startPosition + TENDENCY_HEIGHT * priceTendency - TOOLTIP_HEIGHT / 2
        return asPx(tooltipPosition)
    }
}

function asPx(value: number) {
    return { transform: `translateY(${value}px)` }
}

function calculatePriceTendency() {
    const priceClassValueMin = props.priceIndicatorClasses[props.priceClass - 1]
    const priceClassValueMax = props.priceIndicatorClasses[props.priceClass]
    const priceRange = priceClassValueMax - priceClassValueMin
    const interval = priceRange / 3
    let currentInterval = priceClassValueMin

    for (let index = 1; index <= 3; index++) {
        currentInterval += interval

        if (inRange(props.price, currentInterval - interval, currentInterval)) {
            return index
        }
    }
}
</script>
